import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { Client, DiagnosisReadDTO } from '../generated/api-client';
import { onHttpRequestEnd, onHttpRequestStart } from './httpLoadingTracker';
import { getAxiosErrorDetail } from '../utils/axiosErrorDetail';

export const API_BASE_URL = process.env.REACT_APP_PUBLIC_BASE_URL
  ? `${process.env.REACT_APP_PUBLIC_BASE_URL}/api`
  : //'https://edai.effai.it/api';
    'http://localhost:8000';

// Create axios instance with interceptors
export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 40000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Il refresh token vive in un cookie HttpOnly impostato dal backend (non è leggibile da JS).
 * Il cookie serve solo agli endpoint di sessione: `withCredentials` viene attivato SOLO per
 * questi, mai globalmente, così non viene inviato a servizi terzi (es. generazione report).
 * In produzione (stesso origin) il browser lo invia comunque; in sviluppo (frontend :3000,
 * backend :8000) è necessario perché la richiesta è cross-origin.
 */
const SESSION_COOKIE_PATH_RE = /\/auth\/(login|refresh|logout|change-password)(?:[?#]|$)/;

const isSessionCookieRequest = (url: string): boolean => {
  const isOwnApi = !/^[a-z][a-z0-9+.-]*:\/\//i.test(url) || url.startsWith(API_BASE_URL);
  return isOwnApi && SESSION_COOKIE_PATH_RE.test(url);
};

// Rimuove il vecchio refresh token (pre-cookie) rimasto in localStorage sui browser già in uso.
localStorage.removeItem('refresh_token');

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    onHttpRequestStart();
    if (isSessionCookieRequest(config.url || '')) {
      config.withCredentials = true;
    }
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    onHttpRequestEnd();
    return Promise.reject(error);
  }
);

// Response interceptor with refresh token logic
axiosInstance.interceptors.response.use(
  response => {
    onHttpRequestEnd();
    return response;
  },
  async (error: AxiosError) => {
    onHttpRequestEnd();
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const url = originalRequest?.url || '';

    // Evita refresh/redirect per le chiamate di auth (login/forgot-password/refresh/logout)
    if (
      url.includes('/auth/login') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/logout')
    ) {
      return Promise.reject(error);
    }

    // Se è un errore 401 e non abbiamo già provato a refreshare
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Se stiamo già refreshando, metti in coda
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Il refresh token è nel cookie HttpOnly: il browser lo invia da solo (withCredentials).
        // Il backend ruota il cookie; nel body arriva soltanto il nuovo access token.
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, undefined, {
          withCredentials: true,
          timeout: 15000,
        });

        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);

        // Aggiorna l'header della richiesta originale
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }

        // Processa la coda
        processQueue(null, access_token);
        isRefreshing = false;

        // Riprova la richiesta originale
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Refresh fallito, logout
        processQueue(refreshError, null);
        isRefreshing = false;
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Create client with the configured axios instance
export const apiClient = new Client(API_BASE_URL, axiosInstance);

/** Timeout dedicato: la creazione diagnosi può richiedere molto (copia da diagnosi precedente). */
export const DIAGNOSIS_CREATE_TIMEOUT_MS = 120_000;

export interface InitDiagnosisPayload {
  customer_id: string;
  site_id: string;
  referent_id: string;
  code: string;
}

export async function createDiagnosis(payload: InitDiagnosisPayload): Promise<DiagnosisReadDTO> {
  const { data } = await axiosInstance.post<DiagnosisReadDTO>('/diagnosis/create', payload, {
    timeout: DIAGNOSIS_CREATE_TIMEOUT_MS,
  });
  return data;
}

/** Mappa vettore energetico (id) -> lista usi energetici (id) dai censimenti della diagnosi. */
export async function fetchEnergyUsesByVector(
  diagnosisId: string
): Promise<Record<string, string[]>> {
  const { data } = await axiosInstance.get<Record<string, string[]>>(
    `/diagnosis/${diagnosisId}/energy_uses_by_vector`
  );
  return data ?? {};
}

/** Tipo per UnitCostOverride (API non generata). */
export interface UnitCostOverrideReadDTO {
  id?: string;
  diagnosis_id?: string;
  energy_vector_id?: string;
  unit_cost?: number;
}
export interface UnitCostOverrideCreateDTO {
  diagnosis_id: string;
  energy_vector_id: string;
  unit_cost: number;
}

export const unitCostOverrideApi = {
  listByDiagnosis: (diagnosisId: string) =>
    axiosInstance
      .get<UnitCostOverrideReadDTO[]>(`/unit-cost-override/search_by_diagnosis_id/${diagnosisId}`)
      .then(r => r.data ?? []),
  create: (body: UnitCostOverrideCreateDTO) =>
    axiosInstance.post<UnitCostOverrideReadDTO>('/unit-cost-override/', body).then(r => r.data),
  update: (id: string, body: UnitCostOverrideCreateDTO) =>
    axiosInstance.put<UnitCostOverrideReadDTO>(`/unit-cost-override/${id}`, body).then(r => r.data),
  delete: (id: string) => axiosInstance.delete(`/unit-cost-override/${id}`),
};

export interface DiagnosisChecklistItem {
  code: string;
  label: string;
  section: string;
  passed: boolean;
  details?: string | null;
}

export interface DiagnosisChecklistResponse {
  diagnosis_id: string;
  checks: DiagnosisChecklistItem[];
}

/** Servizio esterno di generazione report (risponde con uno zip). */
export const REPORT_GENERATION_BASE_URL =
  process.env.REACT_APP_REPORT_GENERATION_URL ||
  'https://cosmo.fedabo.com:444/api/ReportGeneration';

/** Media ~10s, massimo ~1 min: margine extra per sicurezza. */
const REPORT_GENERATION_TIMEOUT_MS = 120000;

/** Estrae il nome file da un header Content-Disposition (supporta `filename*=UTF-8''...`). */
function parseContentDispositionFilename(header: unknown): string | null {
  if (typeof header !== 'string' || !header) return null;
  const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      /* ignora e prova il formato semplice */
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/.exec(header);
  return plain?.[1]?.trim() || null;
}

/**
 * Client per l'autofill AI del campo "Inquadramento sito".
 *
 * Due modalità di generazione, speculari ai due endpoint backend su
 * /ai/generate-process-description/{id}:
 *   - `generateOnline`         → GET,  ricerca online (Google Search grounding).
 *   - `generateFromDocuments`  → POST, estrazione testo da file PDF/Word caricati.
 * La lettura del testo già salvato in DB è GET /ai/process-description/{id}.
 *
 * Entrambe usano un timeout dedicato più lungo del default dell'istanza axios
 * (40s), perché la generazione AI — specialmente con ricerca web o estrazione
 * di documenti — può richiedere più tempo di una normale chiamata CRUD.
 */
export const SITE_FRAMING_AI_TIMEOUT_MS = 120_000;

/** Errore di validazione documenti sollevato dal backend (HTTP 422), già tipizzato. */
export interface SiteFramingAiDocumentError {
  code: string;
  message: string;
  filename?: string | null;
}

/** Estrae `{ code, message, filename }` se la risposta è l'errore 422 tipizzato dei documenti. */
export function getSiteFramingAiDocumentError(error: unknown): SiteFramingAiDocumentError | null {
  if (!(error instanceof AxiosError)) {
    return null;
  }
  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  if (
    detail &&
    typeof detail === 'object' &&
    'message' in detail &&
    typeof (detail as { message?: unknown }).message === 'string'
  ) {
    const typed = detail as { code?: string; message: string; filename?: string | null };
    return { code: typed.code || 'validation_error', message: typed.message, filename: typed.filename ?? null };
  }
  return null;
}

/** Messaggio utente da mostrare per un errore della generazione AI dell'inquadramento sito. */
export function getSiteFramingAiErrorMessage(error: unknown, fallback: string): string {
  const documentError = getSiteFramingAiDocumentError(error);
  if (documentError) {
    return documentError.message;
  }
  if (error instanceof AxiosError && error.code === 'ECONNABORTED') {
    return 'La generazione AI ha richiesto troppo tempo: riprova.';
  }
  if (error instanceof AxiosError && error.message === 'canceled') {
    return 'Richiesta annullata';
  }
  return getAxiosErrorDetail(error, fallback);
}

export const siteFramingAiApi = {
  /** Genera l'inquadramento sito tramite ricerca online (Google Search grounding). */
  generateOnline: (diagnosisId: string, signal?: AbortSignal) =>
    axiosInstance
      .get<string>(`/ai/generate-process-description/${diagnosisId}`, {
        timeout: SITE_FRAMING_AI_TIMEOUT_MS,
        signal,
      })
      .then(r => r.data),

  /** Genera l'inquadramento sito a partire da documenti PDF/Word caricati (max 2 file). */
  generateFromDocuments: (
    diagnosisId: string,
    files: File[],
    options?: { signal?: AbortSignal; onUploadProgress?: (percent: number) => void }
  ) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file, file.name));
    return axiosInstance
      .post<string>(`/ai/generate-process-description/${diagnosisId}`, formData, {
        timeout: SITE_FRAMING_AI_TIMEOUT_MS,
        signal: options?.signal,
        // L'istanza ha Content-Type: application/json di default e il
        // transformRequest di axios, vedendolo, converte il FormData in JSON:
        // il server non riceverebbe `files` (422 "Field required"). Con
        // multipart/form-data il FormData resta intatto e il browser aggiunge
        // da solo il boundary.
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: evt => {
          if (options?.onUploadProgress && typeof evt.total === 'number' && evt.total > 0) {
            options.onUploadProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      })
      .then(r => r.data);
  },
};

export const diagnosisCloseExportApi = {
  getFinalChecks: (diagnosisId: string) =>
    axiosInstance
      .get<DiagnosisChecklistResponse>(`/diagnosis-close-export/${diagnosisId}/final-checks`)
      .then(r => r.data),
  getRequiredFieldsChecklist: (diagnosisId: string) =>
    axiosInstance
      .get<DiagnosisChecklistResponse>(
        `/diagnosis-close-export/${diagnosisId}/required-fields-checklist`
      )
      .then(r => r.data),
  closeDiagnosis: (diagnosisId: string) =>
    axiosInstance.post(`/diagnosis/${diagnosisId}/close`).then(r => r.data),
  /**
   * Genera il report della diagnosi sul servizio esterno (ReportGeneration) e restituisce lo zip.
   * Il servizio riceve il JWT della sessione corrente (Bearer) e lo usa per leggere i dati.
   * Tempo medio ~10s, massimo ~1 min.
   */
  generateReport: async (diagnosisId: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await axiosInstance.get<Blob>(
      `${REPORT_GENERATION_BASE_URL}/generate/${encodeURIComponent(diagnosisId)}`,
      { responseType: 'blob', timeout: REPORT_GENERATION_TIMEOUT_MS }
    );
    return {
      blob: response.data,
      filename:
        parseContentDispositionFilename(response.headers?.['content-disposition']) ??
        `report_diagnosi_${diagnosisId}.zip`,
    };
  },
};
